export const MANUAL_INSTALLMENT_PENDING = 'in attesa di pagamento';
export const MANUAL_INSTALLMENT_PAID = 'pagato';
export const MANUAL_INSTALLMENT_EXPIRED = 'scaduta';
export const MANUAL_INSTALLMENT_CANCELLED = 'annullata';

export function normalizeManualInstallmentStatus(value) {
  return (value || MANUAL_INSTALLMENT_PENDING).toString().trim().toLowerCase();
}

export function normalizeManualInstallmentMethod(value) {
  return (value || '').toString().trim().toLowerCase();
}

export function normalizeManualInstallmentsOrderStatus(value) {
  return (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function isManualInstallmentsTerminalStatus(value) {
  const normalized = normalizeManualInstallmentsOrderStatus(value);
  if (!normalized) return false;

  // Un rimborso parziale non rende automaticamente non dovuto il saldo restante.
  const isPartialRefund = (
    (normalized.includes('rimbors') || normalized.includes('refund'))
    && (normalized.includes('parzial') || normalized.includes('partial'))
  );
  if (isPartialRefund) return false;

  const statusTokens = new Set(normalized.split(' ').filter(Boolean));
  const terminalTokens = [
    'void',
    'voided',
    'refused',
    'declined',
    'denied',
    'failed',
    'expired',
    'scaduto',
  ];

  return (
    normalized.includes('cancellat')
    || normalized.includes('annullat')
    || normalized.includes('cancelled')
    || normalized.includes('canceled')
    || normalized.includes('rimborsat')
    || normalized.includes('refunded')
    || terminalTokens.some(token => statusTokens.has(token))
  );
}

export function isManualInstallmentsOrderTerminal(order = {}) {
  if (typeof order === 'string') return isManualInstallmentsTerminalStatus(order);
  if (!order || typeof order !== 'object') return false;

  return [
    order.status,
    order.orderStatus,
    order.state,
    order.paymentStatus,
    order.payment_status,
  ].some(isManualInstallmentsTerminalStatus);
}

export function isManualInstallmentCancelledStatus(value) {
  const normalized = normalizeManualInstallmentsOrderStatus(value);
  if (!normalized) return false;
  return (
    normalized === MANUAL_INSTALLMENT_CANCELLED
    || normalized.includes('annullat')
    || normalized.includes('cancellat')
    || normalized.includes('cancelled')
    || normalized.includes('canceled')
    || normalized.includes('non dovut')
    || normalized === 'void'
    || normalized === 'voided'
  );
}

export function isManualPaymentProofMethod(value) {
  const normalized = normalizeManualInstallmentMethod(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  return [
    'bonifico',
    'bonifico bancario',
    'bank transfer',
    'bacs',
    'addebito diretto',
    'direct debit',
  ].includes(normalized);
}

export function parseManualInstallmentDueDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const parsed = new Date(`${raw}T23:59:59`);
    const ms = parsed.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  const parsed = new Date(raw.replace(' ', 'T'));
  const ms = parsed.getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function isFutureManualInstallmentDue(value, now = Date.now()) {
  const due = parseManualInstallmentDueDate(value);
  return !!due && due > now;
}

export function deriveManualInstallmentStatus(inst = {}, now = Date.now(), order = {}) {
  const normalized = normalizeManualInstallmentStatus(inst?.status);
  const isPaidStatus = [MANUAL_INSTALLMENT_PAID, 'pagata', 'paid', 'completed'].includes(normalized);
  if (inst?.paidAt || inst?.paid_at || isPaidStatus) return MANUAL_INSTALLMENT_PAID;
  if (
    isManualInstallmentCancelledStatus(normalized)
    || isManualInstallmentsOrderTerminal(order)
  ) return MANUAL_INSTALLMENT_CANCELLED;
  if (isFutureManualInstallmentDue(inst?.scheduledDate, now)) return MANUAL_INSTALLMENT_PENDING;
  return normalized || MANUAL_INSTALLMENT_PENDING;
}

export function isManualInstallmentPaid(inst = {}, now = Date.now(), order = {}) {
  return deriveManualInstallmentStatus(inst, now, order) === MANUAL_INSTALLMENT_PAID;
}

export function isManualInstallmentOverdue(inst = {}, now = Date.now(), order = {}) {
  if (!inst) return false;
  const status = deriveManualInstallmentStatus(inst, now, order);
  if (status === MANUAL_INSTALLMENT_PAID || status === MANUAL_INSTALLMENT_CANCELLED) return false;
  if (inst?.overdue === true) return true;
  const due = parseManualInstallmentDueDate(inst?.scheduledDate);
  return !!due && due < now;
}

export function isManualInstallmentOperationallyExpired(inst = {}, now = Date.now(), order = {}) {
  const status = deriveManualInstallmentStatus(inst, now, order);
  return status === MANUAL_INSTALLMENT_EXPIRED
    || (status === MANUAL_INSTALLMENT_PENDING && isManualInstallmentOverdue(inst, now, order));
}

export function isManualInstallmentOperationallyPending(inst = {}, now = Date.now(), order = {}) {
  return deriveManualInstallmentStatus(inst, now, order) === MANUAL_INSTALLMENT_PENDING
    && !isManualInstallmentOperationallyExpired(inst, now, order);
}

export function manualInstallmentPaymentProof(inst = {}, order = {}) {
  return inst?.paymentProof || order?.paymentProof || null;
}

export function manualInstallmentProofLabel(proof = {}) {
  if (!proof) return 'Apri documento';
  const name = (proof.fileName || '').trim();
  if (name) return name;
  const type = (proof.fileType || proof.mimeType || '').trim();
  if (type) {
    const last = type.split('/').pop();
    return `Documento ${last || type}`;
  }
  return 'Apri documento';
}

export function manualInstallmentStats(plan = [], now = Date.now(), order = {}) {
  const list = Array.isArray(plan) ? plan : [];
  const stats = {
    total: list.length,
    paid: 0,
    expired: 0,
    pending: 0,
    cancelled: 0,
    settled: false,
  };

  list.forEach((inst) => {
    const status = deriveManualInstallmentStatus(inst, now, order);
    if (status === MANUAL_INSTALLMENT_PAID) stats.paid += 1;
    if (status === MANUAL_INSTALLMENT_CANCELLED) stats.cancelled += 1;
    if (isManualInstallmentOperationallyExpired(inst, now, order)) stats.expired += 1;
    if (isManualInstallmentOperationallyPending(inst, now, order)) stats.pending += 1;
  });

  stats.settled = stats.total > 0 && stats.paid === stats.total;
  return stats;
}

export function manualInstallmentsOrderState(plan = [], now = Date.now()) {
  const stats = manualInstallmentStats(plan, now);
  const hasPaid = stats.paid > 0;
  const allPaid = stats.total > 0 && stats.paid === stats.total;

  if (!hasPaid) {
    return {
      total: stats.total,
      paid: stats.paid,
      hasPaid,
      allPaid,
      status: MANUAL_INSTALLMENT_PENDING,
      paymentStatus: 'pending',
    };
  }

  if (allPaid) {
    return {
      total: stats.total,
      paid: stats.paid,
      hasPaid,
      allPaid,
      status: MANUAL_INSTALLMENT_PAID,
      paymentStatus: 'completed',
    };
  }

  return {
    total: stats.total,
    paid: stats.paid,
    hasPaid,
    allPaid,
    status: 'rateizzato',
    paymentStatus: 'pending',
  };
}
